import moment from 'moment'
import { type FragmentType, graphql, useFragment } from '@/gql'
import type {
  ComplianceData,
  RecruiterComplianceStatus,
  VerifiedWorkerComplianceStatus,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

export const VerifiedComplianceStatus = graphql(`
  fragment VerifiedComplianceStatus on ComplianceDataStatus {
    statusValue {
      ... on VerifiedWorkerStatusValue {
        workerStatus: status
      }
      ... on RecruiterStatusValue {
        recruiterStatus: status
      }
    }
  }
`)

export const VerifiedComplianceDate = graphql(`
  fragment VerifiedComplianceDate on ComplianceDataDate {
    key
    date
  }
`)

export const VerifiedComplianceUser = graphql(`
  fragment VerifiedComplianceUser on ComplianceDataUser {
    user {
      id
      name
    }
  }
`)

export const VerifiedComplianceMessage = graphql(`
  fragment VerifiedComplianceMessage on ComplianceDataField {
    label
    value
  }
`)

// Mirror ComplianceDataDateKey on the backend.
export const VerifiedComplianceDateKey = {
  ReviewedAt: 'reviewed_at',
  NextReviewAt: 'next_review_at',
} as const

// moment parses a calendar day (`YYYY-MM-DD`) as local midnight, so the day never shifts.
export const formatReviewDate = (date: string): string => moment(date).format('MMM D, YYYY')

type ReviewStatus = VerifiedWorkerComplianceStatus | RecruiterComplianceStatus

// Worker and staffing agency statuses share their values.
export const reviewStatusText = (status: ReviewStatus | null): string =>
  ({
    INCOMPLETE: $t('Incomplete'),
    READY_TO_REVIEW: $t('Ready to review'),
    CHANGES_REQUESTED: $t('Changes requested'),
    CLEARED: $t('Cleared'),
  })[status ?? 'INCOMPLETE']

type Entry =
  | ({ __typename?: 'ComplianceDataStatus' } & FragmentType<typeof VerifiedComplianceStatus>)
  | ({ __typename?: 'ComplianceDataDate' } & FragmentType<typeof VerifiedComplianceDate>)
  | ({ __typename?: 'ComplianceDataUser' } & FragmentType<typeof VerifiedComplianceUser>)
  | ({ __typename?: 'ComplianceDataField' } & FragmentType<typeof VerifiedComplianceMessage>)
  | {
      __typename?: Exclude<
        ComplianceData['__typename'],
        'ComplianceDataStatus' | 'ComplianceDataDate' | 'ComplianceDataUser' | 'ComplianceDataField'
      >
    }

type Entries = ReadonlyArray<Entry> | null | undefined

export function complianceStatus(data: Entries): ReviewStatus | null {
  for (const entry of data ?? []) {
    if (entry.__typename === 'ComplianceDataStatus') {
      const { statusValue } = useFragment(VerifiedComplianceStatus, entry)
      if ('workerStatus' in statusValue) return statusValue.workerStatus
      if ('recruiterStatus' in statusValue) return statusValue.recruiterStatus
    }
  }
  return null
}

export function complianceDate(
  data: Entries,
  key: (typeof VerifiedComplianceDateKey)[keyof typeof VerifiedComplianceDateKey],
): string | null {
  const dates = (data ?? []).flatMap((entry) =>
    entry.__typename === 'ComplianceDataDate' ? [useFragment(VerifiedComplianceDate, entry)] : [],
  )
  return dates.find((date) => date.key === key)?.date ?? null
}

export function complianceReviewerName(data: Entries): string | null {
  for (const entry of data ?? []) {
    if (entry.__typename === 'ComplianceDataUser')
      return useFragment(VerifiedComplianceUser, entry).user.name
  }
  return null
}

// The message stays a plain field; its label is translated by the server from the same strings as $t.
export function complianceMessage(
  data: Entries,
  label: 'Message to worker' | 'Message to staffing agency',
): string | null {
  const fields = (data ?? []).flatMap((entry) =>
    entry.__typename === 'ComplianceDataField'
      ? [useFragment(VerifiedComplianceMessage, entry)]
      : [],
  )
  return fields.find((field) => field.label === $t(label))?.value ?? null
}
